import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Receipt, Loader2, CheckCircle2, XCircle, MessageSquarePlus, Landmark, Split } from "lucide-react";
import { queueItem } from "@/lib/offlineQueue";
import { useOfflineSync } from "@/hooks/useOfflineSync";
import OfflineBadge from "@/components/OfflineBadge";

const TYPES = ["repas", "transport", "hebergement", "fourniture", "deplacement", "autre"];
const STATUTS = {
  brouillon: { label: "Brouillon", cls: "bg-slate-100 text-slate-600" },
  soumis: { label: "Soumis", cls: "bg-blue-100 text-blue-700" },
  complement_demande: { label: "Complément demandé", cls: "bg-amber-100 text-amber-800" },
  approuve: { label: "Approuvé", cls: "bg-emerald-100 text-emerald-800" },
  rejete: { label: "Rejeté", cls: "bg-red-100 text-red-700" },
};
const REMB = {
  a_rembourser: { label: "À rembourser", cls: "bg-amber-50 text-amber-800 border-amber-300" },
  partiellement_rembourse: { label: "Partiellement remboursé", cls: "bg-blue-50 text-blue-800 border-blue-300" },
  rembourse: { label: "Remboursé", cls: "bg-emerald-50 text-emerald-800 border-emerald-300" },
  non_remboursable_avance_tiers: { label: "Réglé directement (non remboursable au salarié)", cls: "bg-slate-50 text-slate-600 border-slate-300" },
};
const EMPTY = { mission_id: "", salarie_id: "", entreprise_id: "", type: "repas", montant: 0, date: new Date().toISOString().slice(0, 10), description: "", avancee_par: "salarie" };

export default function Frais() {
  const { user } = useAuth();
  const canDecide = ["admin", "gestionnaire_ge", "finance"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [missions, setMissions] = useState([]);
  const [entreprises, setEntreprises] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [filtre, setFiltre] = useState("a_traiter");
  const [detail, setDetail] = useState(null);
  const [decisionForm, setDecisionForm] = useState({ decision: "approuve", motif: "", montant_approuve: "" });
  const [imputForm, setImputForm] = useState([{ entreprise_id: "", montant: "", nature: "charge" }]);
  const [rembForm, setRembForm] = useState({ montant: "", date: new Date().toISOString().slice(0, 10), reference: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const reqs = [api.get("/frais")];
      if (canDecide) reqs.push(api.get("/missions"), api.get("/entreprises"), api.get("/salaries"));
      const [a, b, c, d] = await Promise.all(reqs);
      setItems(a.data);
      if (canDecide) { setMissions(b.data); setEntreprises(c.data); setSalaries(d.data); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [canDecide]);
  useEffect(() => { load(); }, [load]);
  useOfflineSync({ onSynced: () => load() });

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, montant: Number(form.montant) };
    Object.keys(payload).forEach(k => { if (payload[k] === "") delete payload[k]; });
    if (!navigator.onLine) {
      try { await queueItem("pending_frais", payload); toast.success("Hors-ligne : frais enregistré localement."); setOpen(false); setForm(EMPTY); }
      catch { toast.error("Enregistrement local impossible"); }
      return;
    }
    try {
      await api.post("/frais/v2", payload);
      toast.success("Frais enregistré en brouillon — utilisez « Soumettre » pour l'envoyer en décision");
      setOpen(false); setForm(EMPTY); setFiltre("tous"); load();
    } catch (err) {
      if (!err.response) {
        try { await queueItem("pending_frais", payload); toast.success("Réseau instable : frais enregistré localement."); setOpen(false); setForm(EMPTY); return; } catch { }
      }
      const det = err.response?.data?.detail;
      toast.error(typeof det === "object" ? det.message : formatApiError(det));
    }
  };

  const soumettre = async (fid) => {
    try { await api.post(`/frais/${fid}/soumettre`); toast.success("Frais soumis pour décision"); load(); setDetail(null); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const decider = async () => {
    const payload = { decision: decisionForm.decision, motif: decisionForm.motif || undefined };
    if (decisionForm.montant_approuve !== "") payload.montant_approuve = Number(decisionForm.montant_approuve);
    try {
      const r = await api.post(`/frais/${detail.id}/decision`, payload);
      toast.success(`Décision : ${STATUTS[r.data.statut]?.label}`);
      setDetail(null); load();
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === "object" ? det.message : formatApiError(det));
    }
  };
  const imputer = async () => {
    const lignes = imputForm.filter(l => l.entreprise_id && l.montant).map(l => ({ ...l, montant: Number(l.montant) }));
    if (!lignes.length) return toast.error("Ajoutez au moins une ligne d'imputation");
    try {
      const r = await api.post(`/frais/${detail.id}/imputations`, { lignes });
      toast.success(`Imputé ${r.data.total_impute} € — ${r.data.explication}`);
      setDetail(null); load();
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === "object" ? det.message : formatApiError(det));
    }
  };
  const rembourser = async () => {
    if (!rembForm.reference.trim()) return toast.error("Une référence de règlement est requise (ex. n° de virement)");
    try {
      const r = await api.post(`/frais/${detail.id}/remboursements`, { ...rembForm, montant: Number(rembForm.montant) });
      toast.success(`Remboursement enregistré (${REMB[r.data.statut_remboursement]?.label}) — enregistrement déclaratif, aucun virement déclenché`);
      setDetail(null); load();
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === "object" ? det.message : formatApiError(det));
    }
  };

  const filtered = items.filter(f => {
    if (filtre === "a_traiter") return ["soumis", "complement_demande"].includes(f.statut);
    if (filtre === "approuves") return f.statut === "approuve";
    if (filtre === "rejetes") return f.statut === "rejete";
    return true;
  });
  const totalApprouve = items.filter(f => f.statut === "approuve").reduce((s, f) => s + Number(f.montant_approuve ?? f.montant ?? 0), 0);
  const salLabel = (id) => { const s = salaries.find(x => x.id === id); return s ? `${s.prenom} ${s.nom}` : "—"; };
  const entLabel = (id) => entreprises.find(x => x.id === id)?.raison_sociale || "—";

  return (
    <div data-testid="frais-page">
      <PageHeader
        title="Frais — circuit complet"
        subtitle={`Dépôt → soumission → décision motivée → imputation → remboursement déclaratif · Total approuvé : ${totalApprouve.toLocaleString("fr-FR")} €`}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <OfflineBadge />
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="frais-add-btn" className="bg-[#C5A059] hover:bg-[#A88846] text-white">
                  <Plus className="h-4 w-4 mr-2" /> Nouveau frais
                </Button>
              </DialogTrigger>
              <DialogContent aria-describedby="frais-new-desc">
                <DialogHeader>
                  <DialogTitle>Déclarer un frais</DialogTitle>
                  <DialogDescription id="frais-new-desc">Enregistré en brouillon, soumis ensuite pour décision. Utilisable hors connexion (synchronisation au retour réseau).</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nature *</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger data-testid="frais-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Montant (€) *</Label><Input type="number" step="0.01" required value={form.montant} onChange={(e) => setForm({ ...form, montant: e.target.value })} data-testid="frais-montant" /></div>
                  <div className="space-y-2"><Label>Date *</Label><Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="frais-date" /></div>
                  <div className="space-y-2">
                    <Label>Avancé par *</Label>
                    <Select value={form.avancee_par} onValueChange={(v) => setForm({ ...form, avancee_par: v })}>
                      <SelectTrigger data-testid="frais-avancee-par"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="salarie">Le salarié</SelectItem>
                        <SelectItem value="ge">Le groupement (réglé directement)</SelectItem>
                        <SelectItem value="entreprise">L'entreprise utilisatrice</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {canDecide && (
                    <>
                      <div className="space-y-2">
                        <Label>Mission / affectation</Label>
                        <Select value={form.mission_id} onValueChange={(v) => setForm({ ...form, mission_id: v })}>
                          <SelectTrigger><SelectValue placeholder="Optionnel" /></SelectTrigger>
                          <SelectContent>{missions.map(m => <SelectItem key={m.id} value={m.id}>{m.libelle}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Salarié</Label>
                        <Select value={form.salarie_id} onValueChange={(v) => setForm({ ...form, salarie_id: v })}>
                          <SelectTrigger><SelectValue placeholder="Optionnel" /></SelectTrigger>
                          <SelectContent>{salaries.map(s => <SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Entreprise</Label>
                        <Select value={form.entreprise_id} onValueChange={(v) => setForm({ ...form, entreprise_id: v })}>
                          <SelectTrigger><SelectValue placeholder="Optionnel" /></SelectTrigger>
                          <SelectContent>{entreprises.map(e2 => <SelectItem key={e2.id} value={e2.id}>{e2.raison_sociale}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                  <div className="space-y-2 col-span-2"><Label>Explication utile</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="frais-description" /></div>
                  <DialogFooter className="col-span-2"><Button type="submit" data-testid="frais-submit" className="bg-[#C5A059] hover:bg-[#A88846] text-white">Enregistrer en brouillon</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <div className="flex gap-2 mb-4 flex-wrap">
        {[["a_traiter", "À traiter"], ["tous", "Tous"], ["approuves", "Approuvés"], ["rejetes", "Rejetés"]].map(([k, lbl]) => (
          <Button key={k} size="sm" variant={filtre === k ? "default" : "outline"} onClick={() => setFiltre(k)}
            className={filtre === k ? "bg-[#1B2A4A] text-white" : ""} data-testid={`frais-filtre-${k}`}>{lbl}</Button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#C5A059]" /></div> :
        filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center" data-testid="frais-empty">
            <Receipt className="h-10 w-10 mx-auto text-slate-300" /><p className="mt-3 text-slate-500">Aucun frais dans ce filtre.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Date</th><th className="px-4 py-3">Nature</th><th className="px-4 py-3">Salarié</th>
                  <th className="px-4 py-3 text-right">Montant</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3">Remboursement</th><th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>{filtered.map(f => (
                <tr key={f.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`frais-row-${f.id}`}>
                  <td className="px-4 py-3 text-slate-700">{f.date}</td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700">{f.type}</span></td>
                  <td className="px-4 py-3 text-slate-600">{salLabel(f.salarie_id)}</td>
                  <td className="px-4 py-3 text-right font-medium text-slate-900">
                    {Number(f.montant).toLocaleString("fr-FR")} €
                    {f.montant_approuve != null && f.montant_approuve !== f.montant && (
                      <div className="text-[10px] text-emerald-700">approuvé {Number(f.montant_approuve).toLocaleString("fr-FR")} €</div>
                    )}
                  </td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded ${STATUTS[f.statut]?.cls || "bg-slate-100"}`}>{STATUTS[f.statut]?.label || f.statut}</span></td>
                  <td className="px-4 py-3">
                    {f.statut_remboursement && <Badge variant="outline" className={REMB[f.statut_remboursement]?.cls}>{REMB[f.statut_remboursement]?.label}</Badge>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => {
                      setDetail(f);
                      setDecisionForm({ decision: "approuve", motif: "", montant_approuve: "" });
                      setImputForm([{ entreprise_id: f.entreprise_id || "", montant: f.montant_approuve ?? f.montant, nature: "charge" }]);
                      const reste = Number(f.montant_approuve ?? f.montant ?? 0) - Number(f.total_rembourse ?? 0);
                      setRembForm({ montant: reste > 0 ? reste : "", date: new Date().toISOString().slice(0, 10), reference: "" });
                    }} data-testid={`frais-open-${f.id}`}>Ouvrir</Button>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}

      <Dialog open={!!detail} onOpenChange={v => { if (!v) setDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="frais-detail-dialog" aria-describedby="frais-detail-desc">
          <DialogHeader>
            <DialogTitle>Frais {detail?.type} — {Number(detail?.montant || 0).toLocaleString("fr-FR")} €</DialogTitle>
            <DialogDescription id="frais-detail-desc">Décision, imputation et remboursement sont trois dimensions distinctes.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-2 bg-slate-50 rounded-lg p-3">
                <div><span className="text-slate-500 text-xs uppercase">Statut</span><div><span className={`text-xs px-2 py-1 rounded ${STATUTS[detail.statut]?.cls}`}>{STATUTS[detail.statut]?.label || detail.statut}</span></div></div>
                <div><span className="text-slate-500 text-xs uppercase">Avancé par</span><div>{{ salarie: "Le salarié", ge: "Le groupement", entreprise: "L'entreprise" }[detail.avancee_par] || "Le salarié"}</div></div>
                <div><span className="text-slate-500 text-xs uppercase">Entreprise</span><div>{entLabel(detail.entreprise_id)}</div></div>
                <div><span className="text-slate-500 text-xs uppercase">Description</span><div>{detail.description || "—"}</div></div>
              </div>

              {detail.decision_motif && (
                <div className="border-l-4 border-amber-400 bg-amber-50 p-3 text-xs" data-testid="frais-motif-visible">
                  <strong>Motif de décision (visible par le salarié) :</strong> {detail.decision_motif}
                </div>
              )}

              {(detail.historique_decisions || []).length > 0 && (
                <div>
                  <div className="text-xs uppercase text-slate-500 font-semibold mb-1">Historique</div>
                  <div className="space-y-1 text-xs" data-testid="frais-historique">
                    {detail.historique_decisions.map((h, i) => (
                      <div key={i} className="bg-slate-50 rounded px-2 py-1">
                        {h.at?.slice(0, 16).replace("T", " ")} — <strong>{h.action}</strong> par {h.par}
                        {h.motif && ` — ${h.motif}`}
                        {h.montant != null && ` — ${h.montant} €`}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!canDecide && ["brouillon", "complement_demande"].includes(detail.statut) && (
                <Button onClick={() => soumettre(detail.id)} data-testid="frais-soumettre-btn" className="bg-[#1B2A4A] text-white w-full">
                  Soumettre pour décision
                </Button>
              )}

              {canDecide && ["soumis", "complement_demande"].includes(detail.statut) && (
                <div className="border-t pt-4 space-y-3" data-testid="frais-decision-zone">
                  <div className="text-xs uppercase text-slate-500 font-semibold">Décision (gestionnaire / finance habilité)</div>
                  <div className="grid grid-cols-3 gap-2">
                    {[["approuve", "Approuver", CheckCircle2, "bg-emerald-600"], ["rejete", "Rejeter", XCircle, "bg-red-600"], ["complement_demande", "Complément", MessageSquarePlus, "bg-amber-500"]].map(([k, lbl, Icon, cls]) => (
                      <Button key={k} variant={decisionForm.decision === k ? "default" : "outline"} onClick={() => setDecisionForm({ ...decisionForm, decision: k })}
                        className={decisionForm.decision === k ? `${cls} text-white` : ""} data-testid={`frais-decision-${k}`}>
                        <Icon className="h-3.5 w-3.5 mr-1" />{lbl}
                      </Button>
                    ))}
                  </div>
                  {decisionForm.decision === "approuve" && (
                    <div className="space-y-1">
                      <Label className="text-xs">Montant approuvé (€) — laisser vide = montant déclaré</Label>
                      <Input type="number" step="0.01" value={decisionForm.montant_approuve} onChange={e => setDecisionForm({ ...decisionForm, montant_approuve: e.target.value })} data-testid="frais-montant-approuve" />
                    </div>
                  )}
                  <div className="space-y-1">
                    <Label className="text-xs">Motif {decisionForm.decision !== "approuve" || decisionForm.montant_approuve !== "" ? "*" : "(optionnel)"}</Label>
                    <Textarea rows={2} value={decisionForm.motif} onChange={e => setDecisionForm({ ...decisionForm, motif: e.target.value })} data-testid="frais-motif" />
                  </div>
                  <Button onClick={decider} data-testid="frais-decision-submit" className="bg-[#1B2A4A] text-white w-full">Enregistrer la décision</Button>
                </div>
              )}

              {canDecide && detail.statut === "approuve" && (
                <div className="border-t pt-4 space-y-4">
                  <div>
                    <div className="text-xs uppercase text-slate-500 font-semibold mb-2 flex items-center gap-1"><Split className="h-3.5 w-3.5" /> Imputation / refacturation</div>
                    {(detail.imputations || []).length > 0 && (
                      <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2 mb-2" data-testid="frais-imputations-resume">
                        Ventilé : {detail.imputations.map(i => `${Number(i.montant).toLocaleString("fr-FR")} € (${i.nature})`).join(" + ")}
                        {detail.montant_non_affecte > 0 && ` — non affecté : ${Number(detail.montant_non_affecte).toLocaleString("fr-FR")} € (${detail.montant_non_affecte_explication})`}
                      </div>
                    )}
                    {imputForm.map((l, i) => (
                      <div key={i} className="flex gap-2 items-end mb-2">
                        <div className="flex-1">
                          <Select value={l.entreprise_id} onValueChange={v => setImputForm(imputForm.map((x, j) => j === i ? { ...x, entreprise_id: v } : x))}>
                            <SelectTrigger data-testid={`imput-ent-${i}`}><SelectValue placeholder="Entreprise" /></SelectTrigger>
                            <SelectContent>{entreprises.map(e2 => <SelectItem key={e2.id} value={e2.id}>{e2.raison_sociale}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <Input type="number" step="0.01" className="w-28" placeholder="Montant" value={l.montant} onChange={e => setImputForm(imputForm.map((x, j) => j === i ? { ...x, montant: e.target.value } : x))} data-testid={`imput-montant-${i}`} />
                        <Select value={l.nature} onValueChange={v => setImputForm(imputForm.map((x, j) => j === i ? { ...x, nature: v } : x))}>
                          <SelectTrigger className="w-36" data-testid={`imput-nature-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="charge">Charge GE</SelectItem><SelectItem value="refacturation">Refacturation</SelectItem></SelectContent>
                        </Select>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setImputForm([...imputForm, { entreprise_id: "", montant: "", nature: "charge" }])} data-testid="imput-add-line">+ Ligne</Button>
                      <Button size="sm" onClick={imputer} data-testid="imput-submit" className="bg-[#1B2A4A] text-white">Enregistrer l'imputation</Button>
                    </div>
                  </div>

                  <div>
                    <div className="text-xs uppercase text-slate-500 font-semibold mb-2 flex items-center gap-1"><Landmark className="h-3.5 w-3.5" /> Remboursement (déclaratif — aucun virement)</div>
                    {(detail.remboursements || []).length > 0 && (
                      <div className="space-y-1 text-xs mb-2" data-testid="frais-remboursements">
                        {detail.remboursements.map(r => (
                          <div key={r.id} className="bg-slate-50 rounded px-2 py-1">{r.date} — <strong>{Number(r.montant).toLocaleString("fr-FR")} €</strong> — réf. {r.reference} — par {r.enregistre_par}</div>
                        ))}
                      </div>
                    )}
                    {!["non_remboursable_avance_tiers", "rembourse"].includes(detail.statut_remboursement) && (
                      <>
                        <div className="grid grid-cols-3 gap-2">
                          <Input type="number" step="0.01" placeholder="Montant" value={rembForm.montant} onChange={e => setRembForm({ ...rembForm, montant: e.target.value })} data-testid="remb-montant" />
                          <Input type="date" value={rembForm.date} onChange={e => setRembForm({ ...rembForm, date: e.target.value })} data-testid="remb-date" />
                          <Input placeholder="Référence règlement *" value={rembForm.reference} onChange={e => setRembForm({ ...rembForm, reference: e.target.value })} data-testid="remb-reference" />
                        </div>
                        <Button size="sm" className="mt-2 bg-[#1B2A4A] text-white" onClick={rembourser} data-testid="remb-submit">Enregistrer le remboursement</Button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
